// Reusable centered modal dialog + a ConfirmDialog built on it. Used to host the
// "+ New …" create forms (kept out of the browsing views) and destructive-action
// confirmations. Closes on Escape and on backdrop click; focus is trapped loosely
// by rendering over a backdrop. Visual style matches the card/btn primitives.

import { useEffect, type ReactNode } from 'react';

export function Modal({
  title,
  onClose,
  children,
  footer,
  width,
}: {
  title: ReactNode;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  width?: number;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="modal-backdrop" onClick={onClose} role="presentation">
      <div
        className="modal"
        style={width ? { width } : undefined}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <header className="modal-head">
          <h2>{title}</h2>
          <button className="btn btn-sm modal-x" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </header>
        <div className="modal-body">{children}</div>
        {footer && <footer className="modal-foot">{footer}</footer>}
      </div>
    </div>
  );
}

// Confirm-before-act dialog (delete/revoke/cancel). The action runs async; the
// confirm button shows a busy label and surfaces a friendly error (e.g. a 403 for
// a non-owner) instead of closing.
export function ConfirmDialog({
  title,
  message,
  confirmLabel = 'Confirm',
  danger = true,
  busy = false,
  error,
  onConfirm,
  onClose,
}: {
  title: ReactNode;
  message: ReactNode;
  confirmLabel?: string;
  danger?: boolean;
  busy?: boolean;
  error?: string | null;
  onConfirm: () => void;
  onClose: () => void;
}) {
  return (
    <Modal
      title={title}
      onClose={busy ? () => {} : onClose}
      width={420}
      footer={
        <>
          <button className="btn btn-sm" disabled={busy} onClick={onClose}>
            Cancel
          </button>
          <button
            className={`btn btn-sm ${danger ? 'btn-danger' : 'btn-primary'}`}
            disabled={busy}
            onClick={onConfirm}
          >
            {busy ? 'Working…' : confirmLabel}
          </button>
        </>
      }
    >
      <p className="confirm-msg">{message}</p>
      {error && <div className="form-error">{error}</div>}
    </Modal>
  );
}
